import type { HomeTodayItem } from "@/api/adapters/home-dashboard";

import { GRADE_BADGE, GRADE_SHORT_KEYS } from "../constants";
import { maskNameKeepLast } from "../utils";
import { EmptyMessage } from "@/components/feedback/EmptyMessage";
import { RiskPill } from "@/components/ui/RiskPill";

interface Props {
	data: HomeTodayItem[];
	t: (key: string, opts?: Record<string, unknown>) => string;
	navigate: (path: string) => void;
}

/** level → the card modifier PRD 02 §2.4 ② names for it. Absent = no band. */
const MODAL_PAT_LEVEL: Record<string, string> = {
	DANGER: " modal-pat--danger",
	WARNING: " modal-pat--warning",
};

export function SeverityDetail({ data: items, t, navigate }: Props) {
	if (!items.length) {
		return <EmptyMessage msg={t("medical:home.sections.todayPro.empty")} />;
	}

	return (
		<>
			{items.map((it) => {
				const cnt = it.patients.length;
				return (
					<div
						key={it.itemId}
						className={`severity-group${cnt === 0 ? " severity-group--empty" : ""}`}
					>
						<div className="severity-group__head">
							<span className="severity-group__name">{it.itemName}</span>
							<span className="severity-group__cyc">({it.cycleLabel})</span>
							<span className="severity-group__count">
								<span className="severity-group__stage">
									{t("medical:home.sections.todayPro.stage3Prefix")}
								</span>{" "}
								<span className="severity-group__num">{cnt}</span>
								<span className="severity-group__count-unit">
									{t("medical:home.sections.todayPro.unit")}
								</span>
							</span>
						</div>
						{cnt === 0 ? (
							<div className="severity-group__empty-note">
								{t("medical:home.sections.todayPro.allDone")}
							</div>
						) : (
							<div className="modal-pat-list">
								{it.patients.map((p) => (
									<div
										key={`${it.itemId}-${p.uid}`}
										// PRD 02 §2.4 ② — the group body is a `modal-pat--danger` / `modal-pat--warning`
										// card list. The card carried no modifier, so this was the one panel of the four
										// with no colour band, and a WARNING responder looked identical to a DANGER one:
										// both wear the same 단계 3 chip, and the band is the only thing that separates
										// them. The names map one to one onto the levels, so nothing is being invented —
										// a level PRD does not name (a G3 row whose level is neither) keeps a plain card.
										className={`modal-pat${MODAL_PAT_LEVEL[p.level] ?? ""}`}
									>
										<div className="modal-pat__main">
											<div className="modal-pat__head">
												<a
													href={`#/patients?selected=${p.uid}&tab=pro`}
													className="modal-pat__name"
													onClick={(e) => {
														e.preventDefault();
														navigate(`/patients?selected=${p.uid}&tab=pro`);
													}}
												>
													<strong>{maskNameKeepLast(p.name)}</strong>
													{p.mrn && <small>({p.mrn})</small>}
													{" · "}
													{p.primaryDxName}
													<span
														className={`grade-chip ${GRADE_BADGE[p.level] || "grade-chip--missed"}`}
													>
														{t(
															GRADE_SHORT_KEYS[p.level] ||
																"medical:home.patientList.grade.noResponseShort",
														)}
													</span>
													{/* SHCT-828 — same pill as 환자 관리; the neutral `grade-chip--r`
													    showed the number without ever reflecting the 위험도.
													    The Grade chip to its left is a different axis (PRO 단계)
													    and stays untouched. */}
													<RiskPill
														riskScore={p.riskScore}
														riskLevel={p.riskLevel}
														t={t}
													/>
												</a>
											</div>
										</div>
										<div className="modal-pat__actions">
											<a
												href={`#/patients?selected=${p.uid}&tab=pro`}
												className="btn btn--xs btn--secondary"
												onClick={(e) => {
													e.preventDefault();
													navigate(`/patients?selected=${p.uid}&tab=pro`);
												}}
											>
												{t("medical:home.detail.goToPatient")}
											</a>
										</div>
									</div>
								))}
							</div>
						)}
					</div>
				);
			})}
		</>
	);
}
